import { Map as IconeDaPlanta, Maximize2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type {
  FrontPiecesView,
  FrontPieceView,
  FrontRoomsView,
} from '@obraplan/core/application/contracts/takeoff';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Input } from '@/components/ui/input';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { cn } from '@/lib/cn';
import { useLive } from '@/lib/use-live';
import { AvisoDaFila } from '@/features/planning/AvisoDaFila';
import { useFilaPorCima } from '@/features/planning/use-fila-por-cima';
import { comodosComAFila, pecasComAFila, syncDaExecucao } from './fila-da-frente';
import { ComodosDaFrente } from './ComodosDaFrente';
import { MarcarComodos, MarcarPecas, PlantaEmTelaCheia } from './PlantaEmTelaCheia';
import { PlantaPintada } from './PlantaPintada';
import {
  chaveDaPeca,
  chaveDoComodo,
  estadoDaPeca,
  estadoDoComodo,
  percentual,
  resumoDasPecas,
} from './planta-pintada';
import { usePranchaDaFrente } from './use-prancha-da-frente';

/**
 * A execução de uma frente × serviço abaixo do item (Fase 34, etapa 2 — D2, D3, D10, D14;
 * redesenho de 21/10/2026).
 *
 * O item do cronograma (pavimento × serviço) e a vista de execução mostram a mesma coisa: o
 * **número grande** — o avanço pelos cômodos, ou quantas peças foram executadas —, a barra, a
 * planta pintada quando o desenho tem contorno, e a grade de ladrilhos (cômodos por apto, ou
 * peças). Lê `GetFrontRooms` e `GetFrontPieces`, as duas abertas a quem vê o cronograma.
 *
 * A planta mora na vista de execução (pedido de 19/10/2026): lá ela se abre em tela cheia, e na
 * tela cheia se marca o cômodo e a peça. No item do cronograma fica a grade, com o atalho para
 * a planta na execução.
 */
export interface ExecucaoDaFrenteProps {
  readonly activityId: string;
  readonly serviceId: string;
  /** "Piso cerâmico · 5º Pavimento" — o nome da planta para quem não a vê. */
  readonly label: string;
  readonly unit?: string | undefined;
  /**
   * `na-tela`: a planta pintada aqui, com a tela cheia para marcar (a vista de execução).
   * `atalho`: sem a planta, com o link para ela na vista de execução (o item do cronograma).
   */
  readonly planta?: 'na-tela' | 'atalho';
  /**
   * A tela cheia controlada pelo pai: a vista de execução põe o botão no cabeçalho dela. Sem
   * estes dois, o botão fica aqui, ao lado da planta.
   */
  readonly cheia?: boolean;
  readonly onCheiaChange?: (open: boolean) => void;
}

/** O que as duas leituras deram, ou a espera. */
type Lido =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly message: string }
  | {
      readonly state: 'ready';
      readonly rooms: FrontRoomsView | undefined;
      readonly pieces: FrontPiecesView | undefined;
    };

/** A partir de quantas peças a lista ganha a busca pelo nome, como no apontamento. */
const BUSCA_A_PARTIR_DE = 12;

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '').toLowerCase();
}

function diaEMes(data: string): string {
  const [, mes, dia] = data.split('-');
  return mes === undefined || dia === undefined ? data : `${dia}/${mes}`;
}

const nf = (valor: number): string => valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export function ExecucaoDaFrente({
  activityId,
  serviceId,
  label,
  unit,
  planta = 'na-tela',
  cheia: cheiaDoPai,
  onCheiaChange,
}: ExecucaoDaFrenteProps) {
  const container = useContainer();
  const canMark = usePermission(PERMISSION.LABOR_CONFIRM);
  const [cheiaPropria, setCheiaPropria] = useState(false);
  const controlada = onCheiaChange !== undefined;
  const cheia = controlada ? (cheiaDoPai ?? false) : cheiaPropria;
  const abrirCheia = (open: boolean): void => {
    if (controlada) onCheiaChange(open);
    else setCheiaPropria(open);
  };
  const { session, activeProjectId } = useSessionState();
  const today = useMemo(() => container.clock.today('America/Sao_Paulo').toString(), [container]);
  const chave = `${activityId}|${serviceId}`;

  const answer = useLive(async () => {
    if (session === undefined || activeProjectId === undefined) return undefined;
    const pedido = { projectId: activeProjectId, activityId, serviceId };
    const [rooms, pieces] = await Promise.all([
      container.getFrontRooms.execute(pedido, session),
      container.getFrontPieces.execute({ ...pedido, date: today }, session),
    ]);
    return { chave, rooms, pieces };
  }, [chave, session?.userId, activeProjectId, today]);

  // A fila do aparelho desta obra (Fase 43, etapa 4): o cômodo marcado sem sinal já aparece
  // pintado, e a peça marcada, executada.
  const fila = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve([])
        : container.outbox.list(session.userId, { projectId: activeProjectId }),
    [session?.userId, activeProjectId],
  );
  const entradas = useFilaPorCima(fila, answer);
  const sync = syncDaExecucao(entradas, activityId, serviceId);

  // A prancha do desenho da frente, por baixo da planta (22/10/2026): o desenho vem com as peças.
  const fundo = usePranchaDaFrente(
    answer !== undefined && answer.chave === chave && answer.pieces.ok
      ? answer.pieces.value.floorPlanId
      : undefined,
  );

  const lido: Lido =
    answer === undefined || answer.chave !== chave
      ? { state: 'loading' }
      : !answer.rooms.ok && !answer.pieces.ok
        ? { state: 'error', message: answer.rooms.error.message }
        : {
            state: 'ready',
            rooms: answer.rooms.ok
              ? comodosComAFila(answer.rooms.value, entradas, activityId, serviceId)
              : undefined,
            pieces: answer.pieces.ok
              ? pecasComAFila(answer.pieces.value, entradas, activityId, serviceId)
              : undefined,
          };

  if (lido.state === 'loading') {
    return (
      <p role="status" className="text-sm text-(--color-content-muted)">
        Carregando a execução…
      </p>
    );
  }
  if (lido.state === 'error') {
    return (
      <p role="alert" className="text-sm text-(--color-state-danger)">
        {lido.message}
      </p>
    );
  }

  const { rooms, pieces } = lido;
  // Os ladrilhos abaixo da planta também marcam (23/10/2026) — na vista de execução, para quem
  // confere a mão de obra; no item do cronograma ficam só para ler.
  const marcarAqui: Marcar | undefined =
    planta === 'na-tela' && canMark ? { activityId, serviceId, date: today } : undefined;

  /** A planta, ou o atalho para ela; e a tela cheia, quando aberta. */
  const comPlanta = (desenho: ReactNode): ReactNode =>
    planta === 'atalho' ? (
      <Link
        to={`/cronograma/execucao?frente=${activityId}&servico=${serviceId}`}
        className={cn(buttonVariants({ variant: 'secondary' }), 'self-start')}
      >
        <IconeDaPlanta aria-hidden="true" />
        Ver a planta e marcar na Execução por cômodo
      </Link>
    ) : (
      <div className="flex flex-col gap-2">
        {sync !== undefined && (
          <AvisoDaFila
            sync={sync}
            pendente={
              sync.count === 1
                ? '1 marca aguardando envio'
                : `${String(sync.count)} marcas aguardando envio`
            }
            recusado="O servidor não aceitou uma marca feita sem sinal"
          />
        )}
        {!controlada && (
          <Button
            type="button"
            variant="secondary"
            className="self-end"
            onClick={() => abrirCheia(true)}
          >
            <Maximize2 aria-hidden="true" />
            {canMark ? 'Tela cheia para marcar' : 'Tela cheia'}
          </Button>
        )}
        {desenho}
        {cheia && (
          <PlantaEmTelaCheia
            activityId={activityId}
            serviceId={serviceId}
            label={label}
            date={today}
            rooms={rooms}
            pieces={pieces}
            canMark={canMark}
            fundo={fundo}
            onClose={() => abrirCheia(false)}
          />
        )}
      </div>
    );

  if (rooms !== undefined && rooms.byRoom) {
    const contam = rooms.rooms.filter((room) => !room.notApplicable);
    const iniciados = contam.filter((room) => (room.percent ?? 0) > 0).length;
    const concluidos = contam.filter((room) => (room.percent ?? 0) >= 100).length;
    const ultimo = rooms.rooms
      .map((room) => room.recordedOn)
      .filter((data): data is string => data !== undefined)
      .sort()
      .at(-1);
    return (
      <div className="flex flex-col gap-5">
        <ResumoDaFrente
          numero={rooms.progressFromRooms === undefined ? '—' : percentual(rooms.progressFromRooms)}
          de={
            rooms.progressFromRooms === undefined
              ? 'nenhum cômodo apontado ainda: o avanço do item continua pela medição ou pelo digitado'
              : rooms.basis === 'IGUAL'
                ? 'pelos cômodos, todos com o mesmo peso'
                : 'pelos cômodos, pesado pela medida'
          }
          pct={rooms.progressFromRooms ?? 0}
          rotuloDaBarra={`${percentual(rooms.progressFromRooms ?? 0)} do serviço no pavimento`}
          meta={
            rooms.rooms.length === 0
              ? undefined
              : `${String(iniciados)} de ${String(contam.length)} cômodos iniciados · ${String(concluidos)} ${concluidos === 1 ? 'concluído' : 'concluídos'}${ultimo === undefined ? '' : ` · último apontamento em ${diaEMes(ultimo)}`}`
          }
          legenda={
            <>
              <Legenda cor="var(--color-content-muted)">não iniciado</Legenda>
              <Legenda cor="var(--color-brand)" pintura={50}>
                em andamento, com o %
              </Legenda>
              <Legenda cor="var(--color-state-ok)" pintura={100}>
                concluído
              </Legenda>
              <Legenda cor="var(--color-state-neutral)" tracejada>
                não se aplica
              </Legenda>
            </>
          }
        />
        {rooms.rooms.length === 0 ? (
          <p className="text-sm text-(--color-content-muted)">
            O pavimento não tem desenho com cômodos: lance o desenho no quantitativo para ver os
            aptos aqui.
          </p>
        ) : (
          <>
            {comPlanta(<PlantaPintada rooms={rooms.rooms} label={label} fundo={fundo} />)}
            <ComodosParaMarcar view={rooms} unit={unit} marcar={marcarAqui} />
          </>
        )}
      </div>
    );
  }

  if (pieces !== undefined && pieces.pieces.length > 0) {
    return (
      <PecasDaFrente
        view={pieces}
        unit={unit}
        marcar={marcarAqui}
        planta={comPlanta(<PlantaPintada pieces={pieces.pieces} label={label} fundo={fundo} />)}
      />
    );
  }

  return (
    <p className="text-sm text-(--color-content-muted)">
      Este serviço não desce a cômodo nem tem peças neste pavimento: o avanço fica no item.
    </p>
  );
}

/** O número grande, a barra, a linha de contagem e a legenda — o cabeçalho das duas formas. */
function ResumoDaFrente({
  numero,
  de,
  pct,
  rotuloDaBarra,
  meta,
  legenda,
}: {
  readonly numero: string;
  readonly de: string;
  readonly pct: number;
  readonly rotuloDaBarra: string;
  readonly meta?: string | undefined;
  readonly legenda: ReactNode;
}) {
  const largura = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-(--color-border-subtle) pb-4">
      <div className="flex min-w-0 flex-col gap-3">
        <p
          className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1"
          data-testid="resumo-da-frente"
        >
          <span className="text-4xl leading-none font-semibold tracking-tight tabular">
            {numero}
          </span>
          <span className="text-[15px] text-(--color-content-muted)">{de}</span>
        </p>
        <div
          role="img"
          aria-label={rotuloDaBarra}
          className="h-1.5 w-full max-w-130 overflow-hidden rounded-full border border-(--color-border-subtle) bg-(--color-surface-sunken)"
        >
          <span
            className="block h-full bg-(--color-state-ok)"
            style={{ width: `${String(largura)}%` }}
          />
        </div>
        {meta !== undefined && (
          <p className="font-condensed text-xs text-(--color-content-muted)">{meta}</p>
        )}
      </div>
      <ul
        aria-label="Legenda"
        className="font-condensed flex flex-wrap gap-x-3.5 gap-y-1 text-[11px] text-(--color-content-muted)"
      >
        {legenda}
      </ul>
    </div>
  );
}

function Legenda({
  cor,
  pintura,
  tracejada = false,
  children,
}: {
  readonly cor: string;
  /** Quanto do quadradinho vai pintado, de 0 a 100. */
  readonly pintura?: number;
  readonly tracejada?: boolean;
  readonly children: ReactNode;
}) {
  return (
    <li className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={cn('inline-block h-3 w-3.5 rounded-sm border', tracejada && 'border-dashed')}
        style={{
          borderColor: cor,
          ...(pintura === undefined
            ? {}
            : {
                background: `linear-gradient(to right, color-mix(in oklab, ${cor} 24%, transparent) ${String(pintura)}%, transparent ${String(pintura)}%)`,
              }),
        }}
      />
      {children}
    </li>
  );
}

/** A frente, o serviço e o dia em que os ladrilhos marcam — hoje. */
interface Marcar {
  readonly activityId: string;
  readonly serviceId: string;
  readonly date: string;
}

/**
 * A escolha feita nos ladrilhos: quantos, "Escolher os que faltam", "Limpar" e o painel de
 * marcar da tela cheia logo abaixo. Fica grudada no topo enquanto a lista rola.
 */
function BarraDaEscolha({
  quantos,
  nome,
  faltam,
  onEscolherFaltam,
  onLimpar,
  children,
}: {
  readonly quantos: number;
  readonly nome: { readonly um: string; readonly varios: string; readonly faltam: string };
  readonly faltam: number;
  readonly onEscolherFaltam: () => void;
  readonly onLimpar: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div
      data-part="barra-da-escolha"
      className="sticky top-0 z-10 flex flex-col gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface) p-3 shadow-sm"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium" aria-live="polite">
          {quantos === 0
            ? `Toque nos ladrilhos para escolher ${nome.varios}`
            : `${String(quantos)} ${quantos === 1 ? nome.um : nome.varios}`}
        </span>
        {faltam > 0 && (
          <Button type="button" size="sm" variant="secondary" onClick={onEscolherFaltam}>
            {nome.faltam} ({String(faltam)})
          </Button>
        )}
        {quantos > 0 && (
          <Button type="button" size="sm" variant="ghost" onClick={onLimpar}>
            Limpar
          </Button>
        )}
      </div>
      {quantos > 0 && children}
    </div>
  );
}

function alternarNa(conjunto: ReadonlySet<string>, chave: string): ReadonlySet<string> {
  const proximo = new Set(conjunto);
  if (proximo.has(chave)) proximo.delete(chave);
  else proximo.add(chave);
  return proximo;
}

/** Os cômodos da frente, com a escolha nos ladrilhos para dar o percentual de uma vez. */
function ComodosParaMarcar({
  view,
  unit,
  marcar,
}: {
  readonly view: FrontRoomsView;
  readonly unit: string | undefined;
  readonly marcar: Marcar | undefined;
}) {
  const [escolhidos, setEscolhidos] = useState<ReadonlySet<string>>(new Set());
  if (marcar === undefined) return <ComodosDaFrente view={view} unit={unit} />;

  const comodos = view.rooms.filter((room) => escolhidos.has(chaveDoComodo(room)));
  const faltam = view.rooms
    .filter((room) => {
      const estado = estadoDoComodo(room);
      return estado === 'NAO_INICIADO' || estado === 'EM_ANDAMENTO';
    })
    .map(chaveDoComodo);
  const limpar = () => setEscolhidos(new Set());

  return (
    <div className="flex flex-col gap-3">
      <BarraDaEscolha
        quantos={comodos.length}
        nome={{
          um: 'cômodo escolhido',
          varios: 'cômodos escolhidos',
          faltam: 'Escolher os que faltam',
        }}
        faltam={faltam.length}
        onEscolherFaltam={() => setEscolhidos((antes) => new Set([...antes, ...faltam]))}
        onLimpar={limpar}
      >
        <MarcarComodos
          key={comodos.map(chaveDoComodo).join(',')}
          rooms={comodos}
          activityId={marcar.activityId}
          serviceId={marcar.serviceId}
          date={marcar.date}
          canMark
          onDone={limpar}
        />
      </BarraDaEscolha>
      <ComodosDaFrente
        view={view}
        unit={unit}
        selected={escolhidos}
        onToggleRoom={(room) => setEscolhidos((antes) => alternarNa(antes, chaveDoComodo(room)))}
      />
    </div>
  );
}

function PecasDaFrente({
  view,
  unit,
  planta,
  marcar,
}: {
  readonly view: FrontPiecesView;
  readonly unit: string | undefined;
  readonly planta: ReactNode;
  readonly marcar?: Marcar | undefined;
}) {
  const [busca, setBusca] = useState('');
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string>>(new Set());
  const resumo = resumoDasPecas(view.pieces);
  const comBusca = view.pieces.length > BUSCA_A_PARTIR_DE;
  const termo = normalizar(busca);
  const visiveis =
    comBusca && termo !== ''
      ? view.pieces.filter((peca) => normalizar(peca.name).includes(termo))
      : view.pieces;
  const medidaTotal = view.pieces.reduce((soma, peca) => soma + (peca.measure ?? 0), 0);
  const medidaFeita = view.pieces
    .filter((peca) => peca.executedOn !== undefined)
    .reduce((soma, peca) => soma + (peca.measure ?? 0), 0);
  const ultima = view.pieces
    .map((peca) => peca.executedOn)
    .filter((data): data is string => data !== undefined)
    .sort()
    .at(-1);
  const unidade = unit ?? view.unitCode;
  const escolhidasNaLista = view.pieces.filter((peca) => escolhidas.has(chaveDaPeca(peca)));
  const faltamNaBusca = visiveis.filter((peca) => peca.executedOn === undefined).map(chaveDaPeca);

  return (
    <div className="flex flex-col gap-4">
      <ResumoDaFrente
        numero={String(resumo.executadas)}
        de={`de ${String(resumo.total)} ${resumo.total === 1 ? 'peça executada' : 'peças executadas'}${
          medidaTotal > 0
            ? ` · ${nf(medidaFeita)} de ${nf(medidaTotal)}${unidade === undefined ? '' : ` ${unidade}`}`
            : ''
        }`}
        pct={resumo.total === 0 ? 0 : (resumo.executadas / resumo.total) * 100}
        rotuloDaBarra={`${String(resumo.executadas)} de ${String(resumo.total)} peças executadas`}
        meta={`${ultima === undefined ? 'Nenhuma peça marcada ainda.' : `Última peça marcada em ${diaEMes(ultima)}.`}${
          resumo.naPlanta === 0
            ? ' O desenho deste pavimento não tem contorno: as peças aparecem em grade.'
            : ''
        }`}
        legenda={
          <>
            <Legenda cor="var(--color-content-muted)">não executada</Legenda>
            <Legenda cor="var(--color-state-ok)" pintura={100}>
              executada, com a data
            </Legenda>
          </>
        }
      />
      {resumo.naPlanta > 0 && planta}
      {comBusca && (
        <Input
          type="search"
          aria-label="Buscar peça pelo nome"
          placeholder="Buscar peça — ex.: AP20"
          className="max-w-80"
          value={busca}
          onChange={(event) => setBusca(event.target.value)}
        />
      )}
      {marcar !== undefined && (
        <BarraDaEscolha
          quantos={escolhidasNaLista.length}
          nome={{
            um: 'peça escolhida',
            varios: 'peças escolhidas',
            // Com a busca, "as que faltam" são as da busca: "R60" e escolher todas de uma vez.
            faltam: termo === '' ? 'Escolher as que faltam' : 'Escolher as da busca que faltam',
          }}
          faltam={faltamNaBusca.length}
          onEscolherFaltam={() => setEscolhidas((antes) => new Set([...antes, ...faltamNaBusca]))}
          onLimpar={() => setEscolhidas(new Set())}
        >
          <MarcarPecas
            escolhidas={escolhidasNaLista}
            activityId={marcar.activityId}
            serviceId={marcar.serviceId}
            date={marcar.date}
            canMark
            onDone={() => setEscolhidas(new Set())}
          />
        </BarraDaEscolha>
      )}
      {visiveis.length === 0 ? (
        <p className="text-xs text-(--color-content-muted)">Nenhuma peça com “{busca.trim()}”.</p>
      ) : (
        <ul
          aria-label="Peças do pavimento"
          className="grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-1.5"
        >
          {visiveis.map((peca) => (
            <PecaLadrilho
              key={peca.name}
              peca={peca}
              unit={unidade}
              escolhida={escolhidas.has(chaveDaPeca(peca))}
              {...(marcar === undefined
                ? {}
                : {
                    onAlternar: () =>
                      setEscolhidas((antes) => alternarNa(antes, chaveDaPeca(peca))),
                  })}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PecaLadrilho({
  peca,
  unit,
  escolhida = false,
  onAlternar,
}: {
  readonly peca: FrontPieceView;
  readonly unit: string | undefined;
  readonly escolhida?: boolean;
  /** Com este, o ladrilho vira botão: clicar põe a peça na escolha, ou tira. */
  readonly onAlternar?: () => void;
}) {
  const feita = estadoDaPeca(peca) === 'EXECUTADA';
  const dica =
    feita && peca.executedOn !== undefined
      ? `Executada em ${diaEMes(peca.executedOn)}`
      : 'Não executada';
  const classe = cn(
    'relative flex min-h-[3.4rem] flex-col justify-between gap-1 overflow-hidden rounded-md border bg-(--color-surface-raised) px-2 py-1.5',
    feita ? 'border-(--color-state-ok)' : 'border-(--color-content-muted)',
  );
  const conteudo = <ConteudoDaPeca peca={peca} unit={unit} feita={feita} />;
  if (onAlternar !== undefined) {
    return (
      <li data-estado={feita ? 'EXECUTADA' : 'NAO_INICIADA'} className="flex">
        <button
          type="button"
          aria-pressed={escolhida}
          title={dica}
          onClick={onAlternar}
          className={cn(
            classe,
            'w-full cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-(--color-brand)',
            escolhida && 'outline-2 outline-offset-1 outline-(--color-brand)',
          )}
        >
          {conteudo}
        </button>
      </li>
    );
  }
  return (
    <li data-estado={feita ? 'EXECUTADA' : 'NAO_INICIADA'} title={dica} className={classe}>
      {conteudo}
    </li>
  );
}

function ConteudoDaPeca({
  peca,
  unit,
  feita,
}: {
  readonly peca: FrontPieceView;
  readonly unit: string | undefined;
  readonly feita: boolean;
}) {
  return (
    <>
      {feita && (
        <span
          aria-hidden="true"
          data-part="pintura"
          className="absolute inset-0 bg-(--color-state-ok)/25"
        />
      )}
      <span className="relative text-[13px] leading-tight font-medium">{peca.name}</span>
      <span className="font-condensed relative text-[11.5px] text-(--color-content-muted) tabular">
        {peca.measure === undefined
          ? 'sem medida'
          : `${nf(peca.measure)}${unit === undefined ? '' : ` ${unit}`}`}
        {feita && peca.executedOn !== undefined && ` · ${diaEMes(peca.executedOn)}`}
      </span>
    </>
  );
}
